
import { createContext, useContext, useState, useEffect, ReactNode, useCallback, useMemo, useRef } from 'react';
import { User } from 'firebase/auth';
import { useAuthState } from 'react-firebase-hooks/auth';
import { auth, db } from '../firebase'; // Import db
import { doc, onSnapshot } from 'firebase/firestore'; // Import onSnapshot
import { getUserProfile, saveUserProfile, updateUserProfile } from '../services/firestoreService';
import { UserProfile } from '../types';
import TermsOfUseModal from '../components/core/TermsOfUseModal';

// Define the shape of our custom token claims to satisfy TypeScript
interface CustomClaims {
    roles?: {
        super_admin?: boolean;
        course_admin?: boolean;
        [key: string]: any; // Allow for other roles
    }
    tid?: string; // Microsoft Tenant ID
}

interface AuthContextType {
    user: User | null | undefined;
    userProfile: UserProfile | null;
    userRoles: string[];
    loading: boolean;
    error: Error | undefined;
    claims: CustomClaims | null;
    refreshUser: () => Promise<void>;
    forceRefreshIdToken: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Helper function to perform a more robust check of the roles object, insensitive to key order.
const rolesAreEqual = (rolesA?: { [key: string]: any }, rolesB?: { [key: string]: any }): boolean => {
    if (!rolesA && !rolesB) return true; // Both are null/undefined
    if (!rolesA || !rolesB) return false; // One is null/undefined

    const keysA = Object.keys(rolesA);
    const keysB = Object.keys(rolesB);

    if (keysA.length !== keysB.length) return false;

    // Check if all keys in A exist in B and have the same stringified value.
    // This handles boolean values and arrays of strings correctly.
    for (const key of keysA) {
        if (!keysB.includes(key) || JSON.stringify(rolesA[key]) !== JSON.stringify(rolesB[key])) {
            return false;
        }
    }
    return true;
};

const requiredTenantId = import.meta.env.VITE_MS_TENANT_ID;

export const AuthProvider = ({ children }: { children: ReactNode }) => {
    const [authUser, authLoading, authError] = useAuthState(auth);
    const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
    const [userRoles, setUserRoles] = useState<string[]>([]);
    const [claims, setClaims] = useState<CustomClaims | null>(null);
    const [claimsLoading, setClaimsLoading] = useState(true);
    const lastProcessedUidRef = useRef<string | null>(null);
    const validatedTenantUidRef = useRef<string | null>(null);
    const validatedTenantIdRef = useRef<string | undefined>(undefined);
    const [showTerms, setShowTerms] = useState(false);

    const CURRENT_TERMS_VERSION = 'v1';

    const fetchUserAndClaims = useCallback(async (user: User) => {
        const isInitialLoadForUser = lastProcessedUidRef.current !== user.uid;
        // Keep protected routes mounted during background token refreshes.
        // Only show the global loading state for the first load of a user session.
        if (isInitialLoadForUser) {
            setClaimsLoading(true);
        }
        try {
            const tokenResult = await user.getIdTokenResult();
            const customClaims = tokenResult.claims as CustomClaims;

            // --- STRICT TENANT CHECK ---
            // If we have a required Tenant ID configured, and the user is signing in via Microsoft,
            // we must verify that the token's 'tid' claim matches the required tenant.
            if (requiredTenantId && user.providerData.some(p => p.providerId === 'microsoft.com')) {
                const tokenTenantId = customClaims.tid;
                const hasValidatedTenantForThisUser = validatedTenantUidRef.current === user.uid;
                const effectiveTenantId = tokenTenantId || (hasValidatedTenantForThisUser ? validatedTenantIdRef.current : undefined);

                // Strict check when we have a tenant ID to validate.
                if (effectiveTenantId && effectiveTenantId !== requiredTenantId) {
                    console.error(`Access Denied: Tenant ID mismatch. Expected ${requiredTenantId}, got ${effectiveTenantId}`);
                    await auth.signOut(); // Immediately invalidate the session
                    // Stop processing and allow the auth state change listener to pick up the signOut
                    return; 
                }

                // Missing `tid` is treated as non-blocking because certain refresh/sign-in flows
                // may omit this claim transiently. We still enforce strict mismatch when tenant is present.
                if (!effectiveTenantId) {
                    console.warn('Microsoft tenant ID missing in token claims; continuing session and relying on backend authorization.');
                } else if (effectiveTenantId) {
                    validatedTenantUidRef.current = user.uid;
                    validatedTenantIdRef.current = effectiveTenantId;
                }
            }
            // ---------------------------

            // Use the robust, order-insensitive comparison function.
            setClaims(prevClaims => {
                if (!rolesAreEqual(prevClaims?.roles, customClaims.roles)) {
                    return customClaims;
                }
                return prevClaims;
            });

            let profile = await getUserProfile(user.uid);
            if (profile) {
                const pendingKey = `synapse_pending_entra_profile_${user.uid}`;
                const pendingRaw = sessionStorage.getItem(pendingKey);
                if (pendingRaw) {
                    try {
                        const pendingUpdates = JSON.parse(pendingRaw) as Partial<UserProfile>;
                        await saveUserProfile(user.uid, pendingUpdates);
                        sessionStorage.removeItem(pendingKey);
                        profile = { ...profile, ...pendingUpdates };
                    } catch (pendingErr) {
                        console.debug('Could not apply deferred Entra profile updates:', pendingErr);
                    }
                }
            }
            setUserProfile(profile);
            if (profile && !profile.termsAccepted) {
                setShowTerms(true);
            }
            
            const allRoles = new Set<string>();
            if (customClaims.roles?.super_admin === true) {
                allRoles.add('super_admin');
            }
            if (profile?.roles) {
                if (Array.isArray(profile.roles)) {
                    profile.roles.forEach(r => {
                        if (typeof r === 'string') allRoles.add(r);
                    });
                } else if (typeof profile.roles === 'object') {
                    Object.entries(profile.roles).forEach(([role, val]) => {
                        if (val === true) {
                            allRoles.add(role);
                        }
                    });
                }
            }
            
            setUserRoles(Array.from(allRoles));
            lastProcessedUidRef.current = user.uid;

        } catch (error) {
            console.error("Error fetching user profile and claims:", error);
            setUserProfile(null);
            setUserRoles([]);
            setClaims(null);
            // Force a full loading pass next time for this user after an error.
            lastProcessedUidRef.current = null;
            validatedTenantUidRef.current = null;
            validatedTenantIdRef.current = undefined;
        } finally {
            if (isInitialLoadForUser) {
                setClaimsLoading(false);
            }
        }
    }, []);

    useEffect(() => {
        const unsubscribe = auth.onIdTokenChanged(async (user) => {
            if (user) {
                await fetchUserAndClaims(user);
            } else {
                setUserProfile(null);
                setUserRoles([]);
                setClaims(null);
                lastProcessedUidRef.current = null;
                validatedTenantUidRef.current = null;
                validatedTenantIdRef.current = undefined;
                setClaimsLoading(false);
            }
        });
        return () => unsubscribe();
    }, [fetchUserAndClaims]);

    // --- Listen for server-side role changes ---
    useEffect(() => {
        if (!authUser) return;

        const metadataRef = doc(db, 'metadata', authUser.uid);
        const unsubscribe = onSnapshot(metadataRef, (doc) => {
            // We only care that the document changed. The content ('refreshTime') is just a trigger.
            // We also don't want to trigger this on the very first load.
            if (doc.exists() && !doc.metadata.hasPendingWrites) {
                console.log('Role change detected, forcing ID token refresh...');
                forceRefreshIdToken();
            }
        });

        return () => unsubscribe();
    }, [authUser]);

    const handleAcceptTerms = async () => {
        if (authUser) {
            await updateUserProfile(authUser.uid, { termsAccepted: new Date(), termsAcceptedVersion: CURRENT_TERMS_VERSION });
            setShowTerms(false);
            refreshUser();
        }
    };

    const refreshUser = useCallback(async () => {
        if (authUser) {
            await fetchUserAndClaims(authUser);
        }
    }, [authUser, fetchUserAndClaims]);
    
    const forceRefreshIdToken = useCallback(async () => {
        if (auth.currentUser) {
            try {
                await auth.currentUser.getIdToken(true); // Force refresh
            } catch (error) {
                console.error("Failed to force refresh ID token:", error);
            }
        }
    }, []);

    const value = useMemo(() => ({
        user: authUser,
        userProfile,
        userRoles,
        loading: authLoading || claimsLoading,
        error: authError,
        claims,
        refreshUser,
        forceRefreshIdToken,
    }), [authUser, userProfile, userRoles, authLoading, claimsLoading, authError, claims, refreshUser, forceRefreshIdToken]);

    return (
        <AuthContext.Provider value={value}>
            {children}
            <TermsOfUseModal isOpen={showTerms} version={CURRENT_TERMS_VERSION} onAccept={handleAcceptTerms} />
        </AuthContext.Provider>
    );
};

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (context === undefined) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};
